import { useCallback, useEffect, useMemo, useState } from "react";

import { getCurrentDialogue } from "@/_pages/ai-assistant/api/getCurrentDialogue";
import { sendProgram } from "@/_pages/ai-assistant/api/sendProgram";
import { sendQuestion } from "@/_pages/ai-assistant/api/sendQuestion";
import { sendRating } from "@/_pages/ai-assistant/api/sendRating";
import type {
  AIAssistantShareType,
  Chat,
  RatingType,
  SubquestionsType,
} from "@/_pages/ai-assistant/model";

type UseAIAssistantChatProps = {
  sharedData: AIAssistantShareType | null;
  onQuestionSent?: () => void;
};

const initChatData = (sharedData: AIAssistantShareType | null): Chat[] => {
  if (!sharedData) return [];

  const parsedSchedule = JSON.parse(sharedData.schedule);
  const parsedQuestion = JSON.parse(sharedData.question);
  const hasSchedule =
    Array.isArray(parsedSchedule) && parsedSchedule.length > 0;
  const parsedAccommodation = JSON.parse(sharedData.accommodation);

  return [
    {
      type: "question",
      text: hasSchedule ? "Составь индивидуальную программу." : parsedQuestion,
      timestamp: sharedData.timestamp,
      key: sharedData.key,
    } as Chat,
    {
      type: "answer",
      text: sharedData.answer,
      schedule: parsedSchedule,
      accommodation: parsedAccommodation,
      question: parsedQuestion,
      timestamp: sharedData.timestamp,
      key: sharedData.key,
      documentId: sharedData.documentId,
      dialogue_id: sharedData.dialogue_id,
      query_response: sharedData.query_response,
    } as Chat,
  ];
};

export const useAIAssistantChat = ({
  sharedData,
  onQuestionSent,
}: UseAIAssistantChatProps) => {
  const [chatData, setChatData] = useState<Chat[]>(() =>
    initChatData(sharedData),
  );
  const [isLoading, setIsLoading] = useState(false);
  const [hasRating, setHasRating] = useState(false);

  const dialogueId = useMemo(() => {
    const answerIds = chatData
      .filter((item) => item.type === "answer" && item.dialogue_id)
      .map((item) => item.dialogue_id as string);

    const uniqueIds = Array.from(new Set(answerIds));

    if (uniqueIds.length === 0) {
      return null;
    }

    if (uniqueIds.length > 1) {
      // eslint-disable-next-line no-console
      console.error(
        "[ChatContent] Найдено несколько разных dialogue_id для ответов:",
        uniqueIds,
      );
      return null;
    }

    return uniqueIds[0];
  }, [chatData]);

  const submitQuestion = useCallback(
    async (prompt: string) => {
      setChatData((prev) => [
        ...prev,
        { text: prompt, type: "question" } as Chat,
      ]);
      setIsLoading(true);

      try {
        const result = await sendQuestion(prompt, dialogueId);

        setChatData((prev) => [
          ...prev,
          {
            data: result.data,
            text: result.content,
            type: "answer",
            question: prompt,
            timestamp: result.timestamp,
            dialogue_id: result.dialogue_id,
            key: result.key,
          } as Chat,
        ]);
      } catch {
        setChatData((prev) => [
          ...prev,
          {
            text: "<h4>Произошла ошибка. Попробуйте заново или позже<h4>",
            type: "answer",
          } as Chat,
        ]);
      } finally {
        setIsLoading(false);
        onQuestionSent?.();
      }
    },
    [dialogueId, onQuestionSent],
  );

  const submitProgram = useCallback(
    async (data: SubquestionsType[]): Promise<void> => {
      setIsLoading(true);

      try {
        const result = await sendProgram(data, dialogueId);

        setChatData((prev) => [
          ...prev,
          {
            text: `<h4>Ваша программа на курорте!</h4>`,
            schedule: result.schedule,
            accommodation: result.accommodation,
            type: "answer",
            question: data,
            timestamp: result.timestamp,
            key: result.dialogue_id,
            dialogue_id: result.dialogue_id,
            query_response: result.query_response,
          } as Chat,
        ]);
      } catch {
        setChatData((prev) => [
          ...prev,
          {
            text: "<h4>Произошла ошибка. Попробуйте заново или позже</h4>",
            type: "answer",
          } as Chat,
        ]);
      } finally {
        setIsLoading(false);
        onQuestionSent?.();
      }
    },
    [dialogueId, onQuestionSent],
  );

  const handleSetRating = useCallback(
    (data: RatingType, dialogue_id: string) => {
      sendRating(data, dialogue_id);
    },
    [],
  );

  const handleRefreshProgram = useCallback(
    (data: Chat["question"]) => {
      if (typeof data === "object" && data !== null) {
        submitProgram(data);
      }
    },
    [submitProgram],
  );

  useEffect(() => {
    const getDialogueRating = async () => {
      if (dialogueId) {
        const data = await getCurrentDialogue(dialogueId);
        setHasRating(!!data?.rating);
      }
    };

    getDialogueRating();
  }, [chatData, dialogueId]);

  return {
    chatData,
    dialogueId,
    handleRefreshProgram,
    handleSetRating,
    hasRating,
    isLoading,
    setChatData,
    submitProgram,
    submitQuestion,
  };
};
